import React, { useContext, useEffect, useRef } from 'react';
import { createGlobalStyle } from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import {
  Route, Switch, useHistory, useLocation,
} from 'react-router-dom';
import window from 'global/window';
import document from 'global/document';
import { AuthClient } from '@adsk/identity-web-sdk';
import axios from 'axios';
import deepmerge from 'deepmerge';
import axiosRetry from 'axios-retry';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import {
  ENVIRONMENT,
  MAINTENANCE_MODE,
  PKCE_CLIENT_ID,
  PKCE_ENV,
  RETRY_COUNT,
  ROUTER_BASENAME,
  styleVariables,
  DATA_SCIENCE_API_HOST,
} from './app/common/constants';
import { maintenanceRoutes } from './app/common/routerConfig';
import handleUnauthRedirect from './app/common/handleUnauthRedirect';
import {
  fetchUserData,
  setExperimentationContext,
  isUserLoggedIn,
  fetchUserProfileData,
  getOA2Token,
  getUserId,
  isUserProfileFetched,
  getExperimentationContext,
  getUserDetails,
} from './app/store/userSlice';
import EnvContext from './app/common/envContext';
import { LoginClickHandler } from './app/common/login';
import logger from './app/common/logger';
import { getEnvironmentConstant } from './app/common/utilities';
import authLib from './app/common/pkce';
import { clearErrorState, getErrorState } from './app/store/errorSlice';
import { getCartCountUrl } from './app/common/cartUtils';
import { initializeHNFMonitor } from './app/common/hnfMonitor';
import useLearnOneRoutes from './app/hooks/useLearnOneRoutes';
import cjsAutoCapture from './app/common/cjsAutoCapture';

axiosRetry(axios, { retries: RETRY_COUNT });

function RouterApp() {
  const location = useLocation();
  const dispatch = useDispatch();
  const envContext = useContext(EnvContext);
  const componentLoader = envContext.COMPONENT_LOADER;
  const env = envContext[ENVIRONMENT];
  const inMaintenanceMode = envContext[MAINTENANCE_MODE];
  const defaultRoutes = useLearnOneRoutes();
  const routes = inMaintenanceMode ? maintenanceRoutes : defaultRoutes;
  const errorState = useSelector(getErrorState);
  const userLoggedIn = useSelector(isUserLoggedIn);
  const userDetails = useSelector(getUserDetails);
  const oa2Token = useSelector(getOA2Token);
  const userId = useSelector(getUserId);
  const isProfileFetched = useSelector(isUserProfileFetched);
  let authValidationInterval;
  const history = useHistory();
  const isFirstLoad = useRef(true);
  const experimentationContext = useSelector(getExperimentationContext);

  const ldClient = useLDClient();

  useEffect(() => {
    if (userLoggedIn && !isProfileFetched && (getEnvironmentConstant({ key: DATA_SCIENCE_API_HOST, env }) && getEnvironmentConstant({ key: DATA_SCIENCE_API_HOST, env }) !== '')) {
      dispatch(fetchUserProfileData({ oa2Token, env, userId }));
    }
  }, [userLoggedIn, isProfileFetched]);

  useEffect(() => {
    if (userLoggedIn && isProfileFetched && ldClient) {
      ldClient.identify({
        kind: 'user',
        key: userId,
        email: userDetails?.email,
      });
    }
  }, [userLoggedIn, isProfileFetched, userId, userDetails, ldClient]);

  useEffect(() => {
    if (!isFirstLoad.current) {
      if (!window.location.hash && window.scrollTo !== undefined) {
        // Wait for next tick
        setTimeout(() => window.scrollTo(0, 0), 0);
      }

      if (errorState.hasError) {
        dispatch(clearErrorState());
      }
    } else {
      isFirstLoad.current = false;
    }
  }, [location]);

  authLib.init(new AuthClient({
    client_id: getEnvironmentConstant({ env, key: PKCE_CLIENT_ID }),
    redirect_uri: `${window?.location?.origin}${getEnvironmentConstant({ key: ROUTER_BASENAME, env })}/auth`,
    useRefreshTokens: true,
    env: getEnvironmentConstant({ env, key: PKCE_ENV }),
    scope: 'data:read openid user-profile:read',
  }));

  const parseExperimentationSettings = () => {
    const data = window.gnosisExperimentation || {};
    const overRideQuery = new URLSearchParams(window.location.search).get('test');
    const dataOverrides = overRideQuery ? Object.assign(...overRideQuery.split(',')?.map((exp) => (exp?.includes(':') ? exp.split(':').reduce((a, v) => { const obj = {}; obj[a] = v; return obj; }) : {}))) : {};

    if (Object.keys(data).length || Object.keys(dataOverrides).length) {
      dispatch(setExperimentationContext({ ...data, ...dataOverrides }));
      logger.info('Found experimentation data: ', JSON.stringify({ ...data, ...dataOverrides }));
    } else {
      logger.info('No experimentation data found on window');
    }
  };

  const validateSignIn = () => {
    // Get User State
    dispatch(fetchUserData(envContext[ENVIRONMENT])).then(
      (userDataResponse) => {
        if (userDataResponse.type === 'userData/fulfilled') {
          if (!userDataResponse.payload.userid || userDataResponse.payload.userid?.length < 4) {
            logger.error(`INVALID UID - ValidateSignIn: |${userDataResponse.payload.userid}|`);
          }
        }
      },
    );
  };

  useEffect(() => {
    // Initialize header/footer monitor
    initializeHNFMonitor();

    if (window.headerConfig) {
      const components = {
        meMenu: {
          onSignIn: () => {
            LoginClickHandler();
          },
          onSignOut: () => {
            authLib.authClient.logout({
              post_logout_redirect_uri: handleUnauthRedirect({ env }),
            });
          },
        },
        cart: {
          useMinicart: false,
          count: 0,
        },
        languageSelector: {
          defaultUrl: `${window.location.origin}/learn`,
        },
      };

      // Add Login / Logout
      window.headerConfig.components = deepmerge(window.headerConfig.components, components);
      if (window.adsk && window.adsk.components && window.adsk.components.universalHeader && window.adsk.components.universalHeader.reRender) {
        window?.adsk?.components?.universalHeader?.reRender(window.headerConfig);
      }

      const cartCountURL = getCartCountUrl(env);
      if (cartCountURL) {
        axios.get(cartCountURL)
          .then((res) => {
            const mergedValue = deepmerge(window.headerConfig, {
              components: {
                cart: {
                  url: res?.data?.cartUrl,
                  count: res?.data?.count || 0,
                },
              },
            });
            window.headerConfig = mergedValue;

            const evt = new CustomEvent('UH_UPDATE_CART', {
              detail: mergedValue.components.cart,
            });
            const updateCart = () => {
              window.dispatchEvent(evt);
              window.removeEventListener('UH_GEOLOCATIONPOPUP_MOUNT', updateCart);
            };
            setTimeout(() => { window.dispatchEvent(evt); }, 0);
            setTimeout(() => { window.dispatchEvent(evt); }, 500);

            window.addEventListener('UH_GEOLOCATIONPOPUP_MOUNT', updateCart);
          }).catch((e) => { logger.error('Failed getting cart count ', e); });
      }
      validateSignIn();
      if (authValidationInterval) {
        clearInterval(authValidationInterval);
      }
      authValidationInterval = setInterval(validateSignIn, 3000000);
    }
    // Add listener for experimentation event triggered by Adobe Target
    window.addEventListener('Gnosis_Experimentation_Set', parseExperimentationSettings);

    // Parse the DOM looking for experimentation data in the event Target loaded first
    parseExperimentationSettings();

    // Initialize CJS Auto-Capture alongside existing analytics
    cjsAutoCapture.initialize(envContext).catch((error) => {
      logger.warn('CJS Auto-Capture initialization failed, but continuing with existing analytics:', error);
    });
    const unListen = history.listen(() => {
      document.getElementById('root').style.minHeight = null;
      unListen();
    });
  }, []);

  return (
    <>
      <GlobalStyles />
      <Switch>
        {routes.map((route) => (
          <Route
            key={route.path}
            path={route.path}
            strict={route.strict || false}
            exact={route.exact || false}
            status={route.status || 200}
            render={(props) => {
              let Comp;
              if (route.experimentComponentKeys) {
                Object.keys(route.experimentComponentKeys).forEach((experimentKey) => {
                  if (experimentationContext[experimentKey]) {
                    Object.keys(route.experimentComponentKeys[experimentKey]).forEach((branchKey) => {
                      if (experimentationContext[experimentKey] === branchKey) {
                        Comp = componentLoader[route.experimentComponentKeys[experimentKey][branchKey]];
                      }
                    });
                  }
                });
              }

              if (!Comp) {
                Comp = componentLoader[route.componentKey];
              }

              // eslint-disable-next-line react/jsx-props-no-spreading
              return (<Comp {...props} />);
            }}
          />
        ))}
      </Switch>

    </>
  );
}

const GlobalStyles = createGlobalStyle` 
  * {
    font-family: 'ArtifaktElement';

  }
  
  .MuiDialog-root > div[role="presentation"] {
      outline: none;
  }
  
  div[role="dialog"] h2.MuiTypography-root.MuiDialogTitle-root{
    padding-bottom: 1.25rem !important;
  }
  // Override for bad box model changes in :focus state for Select component
  li.MuiMenuItem-root {
    margin: 0 0.125rem !important;
    padding: 0.5rem 0.75rem !important;
  }
  .container .universal-footer .page-container {
    max-width: calc(1470px - 2em);
    margin: 0 auto;
    
  }
  .container .universal-footer .mega-footer-column__title p {
    margin: 0;
  }
  html, body {
    height: 100%;
  }
  
  .container{
    min-height: 100%;
  }
  html {
    font-size: 16px !important;

    #lo-uh-container {
      background: ${styleVariables.inkOnBackground};
      height: 98px;

      .uh-container-wrapper .uh-container-section-wrapper {
        margin: 0;
        width: auto;
      }
    }
    
    #lo-uf-container {
      min-height: 416px;
    }


    @media only screen and (max-width: 1040px) {
      #lo-uh-container {
        height: 57px;
      }
      
      #lo-uf-container {
        min-height: 357px;
        margin-top: -357px;
      }
    }

    

    .maxWidth {
      max-width: 1440px;
      margin: 0 auto;
    }

    .rightPadding {
      padding-right: 15px;
    }

    .leftPadding {
      padding-left: 15px;
    }
    .topPadding {
      padding-top: 24px;
    }

    @media only screen and (max-width: 1000px) {
      .rightPadding {
        padding-right: 16px;
      }

      .leftPadding {
        padding-left: 16px;
      }
    }

    .MuiAutocomplete-popper {
      z-index: 90 !important;
      &.coaching-catalog-filter-popper {
        z-index: 1001 !important;
      }
      .MuiListSubheader-sticky {
        background-color: ${styleVariables.background} !important;
      }
    }
  }
  
  div.MuiTooltip-tooltip {
    color: ${styleVariables.background};
    border-radius: 0px;
    font-size: 12px;
  }
  .MuiGrid-root.MuiGrid-container {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }
  div.DhigFlyout {
    border-top-left-radius: 1px !important;
    border-top-right-radius: 1px !important;
    margin-top: -2px !important;
    padding-top: 0px;
    
    .Mui-selected {
      svg {
        display: none;
      }
      background-color: transparent;
    }
    
    .subNavMenuItem {
      width: 100%;
      margin: -10px -16px -10px;
      a {
        width: 100%;
        padding: 10px 16px 10px;
        display: block;        
      }
    }
    
    
    .MuiTooltip-tooltipPlacementBottom {
      box-shadow: 0px 2px 0px 0px rgb(0 0 0 / 60%), 0px 0px 0px 0px rgb(0 0 0 / 30%) inset, 1px 0px 0px 0px rgb(0 0 0 / 30%) inset, -1px 0px 0px 0px rgb(0 0 0 / 30%) inset;
      border-top: 1px solid ${styleVariables.lightDivider}; !important;
      padding-top: 0px !important;
      margin-top: 0px !important;
      border-top-left-radius: 1px !important;
      border-top-right-radius: 1px !important;
    }
  }
  
  #uh-pn-container{
    max-width: 92% !important;
    width: calc(1440px + 1.75rem);
  }
  #uh-container, .universal-footer .page-container-wrapper{
    max-width: 90% !important;
    width: 1440px;
    padding: 1rem 0;
  }
  .uh-container-wrapper .uh-container-section-wrapper .uh-container .uh-top-left-container .uh-hamburger-separator-container{
    margin-left: 0 !important;
  }

  @media only screen and (max-width: 399px) {
    .maxWidth{
      padding-left: 1rem;
      padding-right: 1rem;
    }
    #uh-container, .universal-footer .page-container-wrapper {
      max-width: 90% !important;
      margin: 0 16px;
    }
    .universal-footer .page-container-wrapper {
      margin: 0 16px;
    }
    #contentRoot {
      padding-bottom: 357px;
    }
  }
  @media only screen and (min-width: 400px) and (max-width: 559px) {
    .maxWidth{
      padding-left: 1rem;
      padding-right: 1rem;
    }
    #uh-container, .universal-footer .page-container-wrapper {
      max-width: 93% !important;
      margin: 0 16px;
    }
    .universal-footer .page-container-wrapper {
      margin: 0 16px;
      padding-left: 1rem;
      padding-right: 1rem;
    }
    #contentRoot {
      padding-bottom: 357px;
    }
  }
  @media only screen and (min-width: 560px) and (max-width: 1039px) {
    .maxWidth{
      padding-left: 1.5rem;
      padding-right: 1.5rem;
    }
    #uh-container, .universal-footer .page-container-wrapper {
      max-width: 95% !important;
      margin: 0 24px;
    }
    .universal-footer .page-container-wrapper {
      margin: 0 24px;
      padding-left: 1.5rem;
      padding-right: 1.5rem;
    }
    #contentRoot {
      padding-bottom: 357px;
    }
  }
  @media only screen and (min-width: 1040px) {
    .maxWidth{
      padding-left: 5%;
      padding-right: 5%;
    }
   
  }
  @media only screen and (min-width: 560px) and (max-width: 1440px) {
    .gutter-container{
      padding-left: 5%;
      padding-right: 5%;
    }
  }
  @media only screen and (min-width: 400px) and (max-width: 559px) {
    .gutter-container{
      padding-left: 1rem;
      padding-right: 1rem;
    }
  }
  @media only screen and (max-width: 399px) {
    .gutter-container{
      padding-left: 1rem;
      padding-right: 1rem;
    }
    @media only screen and (min-width: 1040px) {
      .gutter-container{
        padding-left: 5%;
        padding-right: 5%;
      }
    }
  }

  .mobileFilterDrawer {
    .filterInnerWrapper {
      padding: 16px;
      .filterHeader {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 16px;
      }
    }
    .filters {
      display: flex;
      flex-direction: column;
      max-height: 60vh;
      overflow-y: auto;
      padding-top: 16px;
      & > div {
        margin-bottom: 16px;
      }
    }
  }
`;

export default RouterApp;
